import { useState } from 'react'
import { GenreAndStyleDropdown } from './GenreAndStyleDropdown'
import { useDispatch } from 'react-redux';
import { createMasteringPreview } from '../../../../../redux/actions/audioEngineer';

export const AdjustSettingsModal = ({ open, onClose, selectedGenreOrStyle, fileData, setLoading }) => {
    const dispatch = useDispatch()
    const [selectedOption, setSelectedOption] = useState('');

    // Multi-select: track each enhancement independently
    const [selectedEnhancements, setSelectedEnhancements] = useState(['loudness', 'tone', 'stereo_field']);

    // "Master for streaming" selected by default
    const [selectedMastering, setSelectedMastering] = useState('master_for_streaming');
    const localLoudness = localStorage.getItem('loudness');
    const localSampleRate = localStorage.getItem('sampleRate');

    if (!open) return null

    const handleSelectedOption = (option) => {
        setSelectedOption(option)
    }

    const toggleEnhancement = (id) => {
        setSelectedEnhancements((prev) =>
            prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]
        )
    }

    const enhancements = [
        { id: 'loudness', label: 'Loudness' },
        { id: 'tone', label: 'Tone' },
        { id: 'stereo_field', label: 'Stereo Field' },
    ]

    const masteringOptions = [
        { id: 'no_mastering', label: 'No mastering' },
        { id: 'master_for_streaming', label: 'Master for streaming' },
        { id: 'master_for_loudness', label: 'Master for loudness' },
    ]

    const handleRegenerate = () => {
        const options = {
            musicalStyle: selectedOption ? selectedOption?.toUpperCase() : selectedGenreOrStyle?.toUpperCase(),
            desiredLoudness: localLoudness,
            sampleRates: [localSampleRate],
        };
        const reqBody = {
            fileUrls: [fileData?.readable_url],
            uploadIds: [fileData?._id],
            options
        }

        dispatch(createMasteringPreview('', reqBody, setLoading));
        onClose()
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60" onClick={onClose} />

            {/* Modal box */}
            <div className="bg-white relative z-10 w-full max-w-2xl rounded-xl shadow-xl overflow-auto m-3 p-4 md:p-8">
                {/* Header */}
                <div className="mb-4 flex flex-col items-start gap-2">
                    <button onClick={onClose} className="h-6 w-6 bg-theme_clr1 text-white/80 hover:text-white rounded-full transition-all">✕</button>
                    <h2 className="text-lg text-theme_clr2 font-normal">Adjust settings</h2>
                </div>

                <div className="body-content text-white overflow-auto">
                    <>
                        <div className="mb-6">
                            <GenreAndStyleDropdown
                                backgroundColor={'bg-theme_clr1'}
                                getSelectedOption={handleSelectedOption}
                                defaultValue={selectedOption ? selectedOption : selectedGenreOrStyle}
                            />
                        </div>

                        {/* Enhancements — multi-select toggle buttons */}
                        <div className="mb-6">
                            <label className="block font-medium text-theme_clr4 text-base mb-2">
                                <i className="bi bi-sliders2 me-1"></i> Enhancements
                            </label>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {enhancements.map(({ id, label }) => {
                                    const isSelected = selectedEnhancements.includes(id)
                                    return (
                                        <button
                                            key={id}
                                            type="button"
                                            onClick={() => toggleEnhancement(id)}
                                            className={`w-full font-medium text-sm text-center rounded-lg px-3 py-3 border transition
                                                ${id === 'stereo_field' ? 'col-span-2 sm:col-span-1' : ''}
                                                ${isSelected
                                                    ? 'bg-theme_clr2 text-white border-theme_clr2'
                                                    : 'bg-theme_clr4/10 text-theme_clr4 border-theme_clr4'
                                                }`}
                                        >
                                            {label}
                                        </button>
                                    )
                                })}
                            </div>
                        </div>

                        {/* Mastering — single-select, defaults to "Master for streaming" */}
                        <div className="mb-8">
                            <label className="block font-medium text-theme_clr4 text-base mb-3">
                                <i className="bi bi-disc me-1"></i> Mastering
                            </label>
                            <div className="flex flex-col">
                                {masteringOptions.map(({ id, label }) => (
                                    <div key={id} className="radio-item relative flex items-center gap-2">
                                        <input
                                            type="radio"
                                            id={id}
                                            name="select-mastering"
                                            className="custom-radio peer !absolute left-3"
                                            checked={selectedMastering === id}
                                            onChange={() => setSelectedMastering(id)}
                                        />
                                        <label
                                            htmlFor={id}
                                            className={`w-full font-medium rounded-lg text-theme_clr4 text-sm ps-10 pr-3 py-3 cursor-pointer
                                                peer-checked:bg-theme_clr1 peer-checked:border-2 border-theme_clr2
                                                ${selectedMastering === id ? 'bg-theme_clr1 border-2 border-theme_clr2' : ''}`}
                                        >
                                            {label}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button type='button' onClick={handleRegenerate} className="w-full bg-theme_clr2 hover:bg-theme_clr4/90 border border-theme_clr1 font-medium rounded-full transition-all px-4 py-3">
                            <i className="bi bi-arrow-clockwise"></i> Regenerate Preview
                        </button>
                    </>
                </div>
            </div>
        </div>
    )
}
